import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { Promotion } from 'reducers/playerPromos';

import PromoDetailsModal from './PromoDetailsModal';
import {
  buildPromoDisplay,
  getCurrentWeekEndDateLabel,
  getCurrentWeekEndWeekdayLabel,
  toRemainingMinutes
} from './promoDisplay';
import { getPromoInlineCopy } from './promoMessaging';
import { getPromoVisualTheme, getTierProgressFill, PROMO_GOLD_METAL } from './promoTheme';

type InlinePromoBarProps = {
  onOpenPromo?: (promo: Promotion) => void;
};

const InlinePromoBar = ({ onOpenPromo }: InlinePromoBarProps = {}) => {
  const { promotions } = useAppSelector((state) => state.playerPromos);
  const { activeView } = useContext(ViewManagerContext);
  const fillRef = useRef<HTMLDivElement>(null);
  const animatedPromoRef = useRef<string | number | null>(null);
  const [selectedPromo, setSelectedPromo] = useState<Promotion | null>(null);
  const activePromo = promotions.find((p: any) => p.is_active);
  const display = activePromo ? buildPromoDisplay(activePromo) : null;
  const activePromoKey = activePromo?.promo_id || activePromo?.promo_name || null;
  const remainingLabel = display ? getRemainingLabel(display) : '';

  useEffect(() => {
    if (!fillRef.current || !display || !activePromoKey) return;

    const width = `${display.progressPct}%`;
    if (animatedPromoRef.current === activePromoKey) {
      gsap.set(fillRef.current, { width });
      return;
    }

    animatedPromoRef.current = activePromoKey;
    gsap.fromTo(
      fillRef.current,
      { width: '0%' },
      { width, duration: 0.8, delay: 0.2, ease: 'power2.out' }
    );
  }, [activePromoKey, display?.progressPct]);

  if (activeView === 'table' || !activePromo || !display) return null;

  const isPosterTheme = activePromo.player_facing_config?.theme === 'parchment';
  const theme = getPromoVisualTheme(display.format, activePromo.player_facing_config?.theme);
  const copy = getPromoInlineCopy({
    promoName: activePromo.promo_name || 'Weekly Promo',
    remainingLabel,
    display,
    playerFacingConfig: activePromo.player_facing_config
  });
  const promoName = activePromo.promo_name || 'Weekly Promo';
  const inlineTitle = copy.title || promoName;
  const promoBadgeLabel = copy.promoBadgeLabel || display.earnedLabel || display.formatLabel;
  const progressLabel = getInlineProgressLabel(display, remainingLabel);
  const showHelperStatus = Boolean(
    ((display.format === 'hybrid_freeroll_qualification' &&
      display.hybridDetails?.state &&
      display.hybridDetails.state !== 'notQualified') ||
      display.tierState) &&
      copy.statusLabel
  );
  const currentWeekEndDate = getCurrentWeekEndDateLabel(activePromo);
  const promoEndTime = '11:59 PM';
  // Two-tier formats (EN-11291): the top tier dresses the badge and the value in the $100K gold.
  const isTopTier = display.tierState === 'bonusAchieved';
  // Cash back (demo 2026-10-26): no CASH BACK badge (the title already says it); the amount
  // being chased sits on a prize ticket at the right, and the time left joins the end date.
  const isCashBack = display.format === 'weekly_cash_back';
  const prizeTier =
    display.tiers.find((tier) => !tier.reached) || display.tiers[display.tiers.length - 1];
  const handleOpenPromo = () => {
    if (onOpenPromo) {
      onOpenPromo(activePromo);
      return;
    }

    setSelectedPromo(activePromo);
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '100%'
      }}>
      <style>{promoBarAnimationCss}</style>
      <div
        className={`promo-inline-banner${isPosterTheme ? ' promo-inline-banner--poster' : ''}${
          isCashBack ? ' promo-inline-banner--cash-back' : ''
        }`}
        style={{
          ...inlinePromoContainer,
          ...(isPosterTheme ? posterPromoContainer : {}),
          background: theme.background,
          borderColor: theme.borderColor,
          boxShadow: theme.boxShadow
        }}
        role='button'
        tabIndex={0}
        onClick={handleOpenPromo}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          handleOpenPromo();
        }}>
        <span
          className='promo-inline-edge-glint promo-inline-edge-glint-top'
          style={theme.edgeGlintStyle}
          aria-hidden='true'
        />
        <span
          className='promo-inline-edge-glint promo-inline-edge-glint-right'
          style={theme.edgeGlintVerticalStyle}
          aria-hidden='true'
        />
        <span
          className='promo-inline-edge-glint promo-inline-edge-glint-bottom'
          style={theme.edgeGlintStyle}
          aria-hidden='true'
        />
        <span
          className='promo-inline-edge-glint promo-inline-edge-glint-left'
          style={theme.edgeGlintVerticalStyle}
          aria-hidden='true'
        />
        {sparkleClassNames.map((className) => (
          <span
            key={className}
            className={className}
            style={theme.sparkleStyle}
            aria-hidden='true'
          />
        ))}
        <div style={{ ...inlineInnerStyle, ...(isPosterTheme ? posterInnerStyle : {}) }}>
          {!isCashBack && (
            <div
              className={isPosterTheme ? 'promo-inline-poster-medallion' : undefined}
              style={{
                ...inlineIconWrap,
                ...(isPosterTheme ? posterIconWrap : {}),
                background: isTopTier ? PROMO_GOLD_METAL : theme.iconBackground,
                borderColor: isTopTier ? 'rgba(59, 32, 8, 0.85)' : theme.iconBorderColor
              }}>
              <span
                style={{
                  ...inlineBadgeText,
                  ...(isPosterTheme ? posterBadgeText : {}),
                  ...(isTopTier ? goldInkText : {})
                }}>
                {promoBadgeLabel}
              </span>
            </div>
          )}

          <div style={{ ...inlineContent, ...(isPosterTheme ? posterContent : {}) }}>
            <div
              className='promo-inline-title-row'
              style={{ ...inlineTitleRow, ...(isPosterTheme ? posterTitleRow : {}) }}>
              <span style={{ ...inlinePromoName, ...(isPosterTheme ? posterPromoName : {}) }}>
                {inlineTitle}
              </span>
              {!isCashBack && (
                <span
                  style={{
                    ...inlineHoursBadge,
                    ...(isPosterTheme ? posterHoursBadge : {}),
                    ...(isTopTier ? { color: GOLD_TEXT } : {})
                  }}>
                  {progressLabel}
                </span>
              )}
            </div>

            <div
              className='promo-inline-track'
              style={{
                ...inlineTrack,
                ...(isPosterTheme ? posterTrack : {}),
                backgroundColor: theme.trackColor
              }}>
              <div
                ref={fillRef}
                style={{
                  ...inlineFill,
                  ...(isPosterTheme ? posterFill : {}),
                  width: `${display.progressPct}%`,
                  background: getTierProgressFill(theme, display.tierState)
                }}
              />
            </div>
            {isCashBack ? (
              <div style={inlineHelperRow}>
                <span
                  className='promo-inline-status'
                  style={{
                    ...inlineCashBackStatus,
                    ...(isTopTier ? { color: GOLD_TEXT } : {})
                  }}>
                  {/* Hours against the tier being chased, as the progress modal shows them
                      ("7.6h / 15h"), plus "· $20 ✓" while chasing $50: a check instead of
                      "earned" keeps the end time on the same line at 390 px (demo 2026-10-28).
                      At the top tier the gold EARNED ticket already says $50, so the hours
                      stand alone. */}
                  {display.tierState === 'qualified' ? (
                    <>
                      {`${display.progressLabel} \u00b7 ${copy.statusLabel}`}
                      <CheckIcon aria-hidden='true' style={inlineCashBackCheck} />
                      <span style={screenReaderOnly}> earned</span>
                    </>
                  ) : (
                    display.progressLabel
                  )}
                </span>
                <span className='promo-inline-helper' style={inlineCashBackEnd}>
                  {/* The window closes the night before the reset day, or on the promotion's
                      end date if that comes first: "Ends Sat 11:59 PM" (demo 2026-10-27). */}
                  {`Ends ${
                    getCurrentWeekEndWeekdayLabel(activePromo) ||
                    getWindowEndDay(display.resetDayLabel)
                  } ${promoEndTime}`}
                </span>
              </div>
            ) : (
              <div style={inlineHelperRow}>
                <span
                  className='promo-inline-helper'
                  style={{ ...inlineHelperText, ...(isPosterTheme ? posterHelperText : {}) }}>
                  {currentWeekEndDate
                    ? `Ends ${currentWeekEndDate}, ${promoEndTime}`
                    : `Ends ${promoEndTime}`}
                </span>
                {showHelperStatus && (
                  <span
                    className='promo-inline-status'
                    style={{
                      ...inlineStatusText,
                      ...(isPosterTheme ? posterStatusText : {}),
                      ...(isTopTier ? { color: GOLD_TEXT } : {})
                    }}>
                    {copy.statusLabel}
                  </span>
                )}
              </div>
            )}
          </div>

          {isCashBack && prizeTier && (
            <div data-testid='promo-prize-ticket' style={prizeTicketShadow}>
              <div
                style={{
                  ...prizeTicketStyle,
                  background: getTierProgressFill(theme, display.tierState),
                  color: isTopTier ? GOLD_INK : theme.onFillColor
                }}>
                <span style={prizeTicketCaption}>{isTopTier ? 'Earned' : 'Earn'}</span>
                <span style={prizeTicketAmount}>{prizeTier.valueLabel}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedPromo && (
        <PromoDetailsModal promo={selectedPromo} onClose={() => setSelectedPromo(null)} />
      )}
    </div>
  );
};

export default InlinePromoBar;

const sparkleClassNames = [
  'promo-inline-sparkle promo-inline-sparkle-one',
  'promo-inline-sparkle promo-inline-sparkle-two',
  'promo-inline-sparkle promo-inline-sparkle-three',
  'promo-inline-sparkle promo-inline-sparkle-four',
  'promo-inline-sparkle promo-inline-sparkle-five'
];

const promoBarAnimationCss = `
  @import url('https://fonts.googleapis.com/css2?family=Rye&family=Special+Elite&family=Oswald:wght@400;500;600;700;800&display=swap');

  .promo-inline-banner {
    width: clamp(360px, 34vw, 640px);
  }

  .promo-inline-banner:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
  }

  .promo-inline-banner--poster {
    width: clamp(520px, 42vw, 650px);
  }

  .promo-inline-banner--poster::before {
    content: "";
    position: absolute;
    inset: 4px;
    border: 1px dashed rgba(74, 35, 10, 0.5);
    border-radius: 5px;
    pointer-events: none;
    z-index: 2;
  }

  .promo-inline-banner--poster::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.18 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    mix-blend-mode: multiply;
    opacity: 0.5;
    z-index: 1;
  }

  .promo-inline-banner--poster .promo-inline-edge-glint,
  .promo-inline-banner--poster .promo-inline-sparkle {
    display: none;
  }

  .promo-inline-poster-medallion {
    /* PERF: the pulsing glow is gone deliberately. An always-on animation keeps
       the whole frame pipeline running at the refresh rate for the entire
       session - measured at ~285% CPU / 133fps on an idle lobby (Galaxy S24+,
       native). box-shadow also cannot be composited, so this one repainted the
       promo banner every frame on top of that. Held at the keyframe's bright
       state instead. See docs/development/game-client-performance.md. */
    box-shadow:
      inset 0 1px 0 rgba(255, 250, 210, 0.9),
      inset 0 -3px 5px rgba(108, 68, 12, 0.3),
      0 5px 14px rgba(34, 18, 4, 0.6),
      0 0 24px rgba(255, 210, 90, 0.62),
      0 0 40px rgba(255, 210, 90, 0.26);
  }

  .promo-inline-poster-medallion::before {
    content: "";
    position: absolute;
    top: -20%;
    bottom: -20%;
    left: -40%;
    width: 32%;
    background: linear-gradient(
      105deg,
      transparent 28%,
      rgba(255,255,255,0) 36%,
      rgba(255,255,255,0.55) 50%,
      rgba(255,255,255,0) 64%,
      transparent 72%
    );
    filter: blur(2px);
    mix-blend-mode: screen;
    pointer-events: none;
    /* PERF: identical sweep at identical speed, fired every 14.4s instead of
       every 3.6s. The sweep occupies the first ~14% of the cycle; the element is
       parked (constant transform, opacity 0) for the rest, so no frames are
       produced in between. */
    animation: promoInlinePosterSheen 14.4s linear infinite;
    will-change: transform, opacity;
  }

  .promo-inline-poster-medallion::after {
    content: "";
    position: absolute;
    top: 4px;
    right: 14px;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(255, 255, 240, 0.95) 0%, rgba(255, 235, 170, 0.5) 40%, transparent 70%);
    box-shadow: 0 0 8px rgba(255, 235, 170, 0.85);
    opacity: 0;
    pointer-events: none;
    /* PERF: always-on sparkle removed - see the medallion rule above. */
    animation: none;
  }

  .promo-inline-edge-glint {
    position: absolute;
    border-radius: 999px;
    background: linear-gradient(90deg, transparent, rgba(255, 246, 203, 0.96), transparent);
    box-shadow: 0 0 10px rgba(255, 232, 154, 0.72);
    opacity: 0;
    pointer-events: none;
    z-index: 1;
  }

  .promo-inline-edge-glint-top,
  .promo-inline-edge-glint-bottom {
    width: 92px;
    height: 2px;
  }

  .promo-inline-edge-glint-right,
  .promo-inline-edge-glint-left {
    width: 2px;
    height: 30px;
    background: linear-gradient(180deg, transparent, rgba(255, 246, 203, 0.96), transparent);
  }

  .promo-inline-edge-glint-top {
    top: 0;
    left: 12px;
    animation: promoInlineGlintTop 4.4s ease-in-out infinite;
  }

  .promo-inline-edge-glint-right {
    top: 8px;
    right: 0;
    animation: promoInlineGlintRight 4.4s ease-in-out 0.55s infinite;
  }

  .promo-inline-edge-glint-bottom {
    right: 12px;
    bottom: 0;
    animation: promoInlineGlintBottom 4.4s ease-in-out 1.1s infinite;
  }

  .promo-inline-edge-glint-left {
    left: 0;
    bottom: 8px;
    animation: promoInlineGlintLeft 4.4s ease-in-out 1.65s infinite;
  }

  .promo-inline-sparkle {
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #fff1b6;
    box-shadow: 0 0 8px rgba(255, 241, 182, 0.9), 0 0 14px rgba(255, 209, 90, 0.45);
    opacity: 0;
    pointer-events: none;
    z-index: 2;
  }

  .promo-inline-sparkle-one {
    top: 9px;
    left: 23%;
    animation: promoInlineSparkle 4.9s ease-in-out 0.2s infinite;
  }

  .promo-inline-sparkle-two {
    top: 29px;
    left: 47%;
    animation: promoInlineSparkle 5.6s ease-in-out 1.4s infinite;
  }

  .promo-inline-sparkle-three {
    top: 13px;
    right: 24%;
    animation: promoInlineSparkle 4.3s ease-in-out 2.1s infinite;
  }

  .promo-inline-sparkle-four {
    right: 11%;
    bottom: 10px;
    animation: promoInlineSparkle 6.1s ease-in-out 0.9s infinite;
  }

  .promo-inline-sparkle-five {
    left: 13%;
    bottom: 8px;
    animation: promoInlineSparkle 5.2s ease-in-out 3s infinite;
  }

  @keyframes promoInlineGlintTop {
    0%, 34%, 100% {
      opacity: 0;
      transform: translateX(0);
    }
    8% {
      opacity: 1;
    }
    22% {
      opacity: 0.95;
      transform: translateX(calc(100% + 250px));
    }
    30% {
      opacity: 0;
      transform: translateX(calc(100% + 250px));
    }
  }

  @keyframes promoInlineGlintRight {
    0%, 34%, 100% {
      opacity: 0;
      transform: translateY(0);
    }
    8% {
      opacity: 1;
    }
    22% {
      opacity: 0.95;
      transform: translateY(26px);
    }
    30% {
      opacity: 0;
      transform: translateY(26px);
    }
  }

  @keyframes promoInlineGlintBottom {
    0%, 34%, 100% {
      opacity: 0;
      transform: translateX(0);
    }
    8% {
      opacity: 1;
    }
    22% {
      opacity: 0.95;
      transform: translateX(calc(-100% - 250px));
    }
    30% {
      opacity: 0;
      transform: translateX(calc(-100% - 250px));
    }
  }

  @keyframes promoInlineGlintLeft {
    0%, 34%, 100% {
      opacity: 0;
      transform: translateY(0);
    }
    8% {
      opacity: 1;
    }
    22% {
      opacity: 0.95;
      transform: translateY(-26px);
    }
    30% {
      opacity: 0;
      transform: translateY(-26px);
    }
  }

  @keyframes promoInlineSparkle {
    0%, 58%, 100% {
      opacity: 0;
      transform: scale(0.4);
    }
    66% {
      opacity: 1;
      transform: scale(1);
    }
    74% {
      opacity: 0.25;
      transform: scale(0.72);
    }
  }


  @keyframes promoInlinePosterSheen {
    /* translateX, not 'left': animating 'left' forces layout every frame.
       Percentages resolve against the ELEMENT's own width (32% of the parent),
       and the sweep travels 150% of the parent, so 150/32 = 468.75%. */
    0% {
      transform: translateX(0);
      opacity: 0;
    }
    2% {
      opacity: 1;
    }
    13.5% {
      transform: translateX(468.75%);
      opacity: 1;
    }
    /* Parked for the remaining ~85%: no value changes, no frames. */
    15%,
    100% {
      transform: translateX(468.75%);
      opacity: 0;
    }
  }


  @media (prefers-reduced-motion: reduce) {
    .promo-inline-edge-glint,
    .promo-inline-sparkle,
    .promo-inline-poster-medallion,
    .promo-inline-poster-medallion::before,
    .promo-inline-poster-medallion::after {
      animation: none;
    }

    .promo-inline-poster-medallion::before,
    .promo-inline-poster-medallion::after {
      opacity: 0;
    }
  }

  @media (max-width: 1280px) {
    .promo-inline-banner {
      width: min(460px, calc(100vw - 24px)) !important;
    }

    .promo-inline-banner--poster {
      width: min(620px, calc(100vw - 24px)) !important;
    }

    .promo-inline-reward {
      display: none !important;
    }
  }

  @media (max-width: 900px) {
    .promo-inline-banner {
      width: min(440px, calc(100vw - 20px)) !important;
      padding: 7px 9px !important;
    }

    .promo-inline-banner--poster {
      width: min(520px, calc(100vw - 20px)) !important;
      padding: 8px 11px !important;
    }

  }

  @media (max-width: 560px) {
    .promo-inline-banner {
      width: min(340px, calc(100vw - 16px)) !important;
    }

    .promo-inline-banner--poster {
      width: min(362px, calc(100vw - 16px)) !important;
      padding: 9px 13px 10px !important;
    }

    .promo-inline-title-row {
      align-items: flex-start;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto;
      row-gap: 3px;
    }
  }
`;

const getRemainingLabel = (display: ReturnType<typeof buildPromoDisplay>): string => {
  // Two-tier formats count to the NEXT tier (EN-11291), not the top one.
  if (display.remainingLabel) return display.remainingLabel;

  const remaining = Math.max(display.targetValue - display.progressValue, 0);

  if (display.format === 'weekly_timeback_credit') {
    return `$${remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }

  return `${Number.isInteger(remaining) ? remaining.toFixed(0) : remaining.toFixed(1)}h`;
};

const getInlineProgressLabel = (
  display: ReturnType<typeof buildPromoDisplay>,
  remainingLabel: string
): string => {
  if (display.format === 'weekly_timeback_credit' || display.remainingLabel) {
    return remainingLabel;
  }

  if (display.targetValue <= 0) {
    return display.progressLabel;
  }

  const remaining = Math.max(display.targetValue - display.progressValue, 0);
  const totalMinutes = toRemainingMinutes(remaining);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}H : ${minutes.toString().padStart(2, '0')}M`;
};

const posterPromoContainer: CSSProperties = {
  maxWidth: 'min(650px, calc(100vw - 24px))',
  padding: '10px 18px 11px',
  borderRadius: '8px',
  color: '#1a0e06'
};

const inlinePromoContainer: CSSProperties = {
  minWidth: 0,
  maxWidth: 'min(640px, calc(100vw - 32px))',
  padding: '8px 12px 8px 10px',
  cursor: 'pointer',
  borderRadius: '8px',
  background:
    'linear-gradient(135deg, rgba(233, 140, 38, 0.95) 0%, rgba(185, 77, 30, 0.88) 56%, rgba(63, 42, 29, 0.96) 100%)',
  border: '1px solid rgba(255, 205, 126, 0.35)',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 22px rgba(0,0,0,0.28), 0 0 18px rgba(233, 140, 38, 0.14)',
  overflow: 'hidden',
  position: 'relative',
  transition: 'transform 0.16s ease, box-shadow 0.16s ease'
};

const inlineInnerStyle: CSSProperties = {
  position: 'relative',
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
};

const posterInnerStyle: CSSProperties = {
  gap: '15px'
};

const inlineIconWrap: CSSProperties = {
  minWidth: 38,
  height: 38,
  padding: '0 8px',
  flexShrink: 0,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(17, 24, 31, 0.28)',
  border: '1px solid rgba(255,255,255,0.22)'
};

const posterIconWrap: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  width: 'auto',
  minWidth: 92,
  maxWidth: '42%',
  height: 46,
  padding: '0 18px',
  borderRadius: 999,
  borderWidth: 2,
  boxShadow:
    'inset 0 1px 0 rgba(255, 250, 210, 0.7), inset 0 -2px 4px rgba(108, 68, 12, 0.35), 0 3px 8px rgba(34, 18, 4, 0.5), 0 0 10px rgba(255, 210, 90, 0.22)'
};

const inlineBadgeText: CSSProperties = {
  color: '#ffffff',
  fontSize: '0.72rem',
  fontWeight: 900,
  lineHeight: 1,
  textAlign: 'center',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

const posterBadgeText: CSSProperties = {
  color: '#3b2008',
  fontFamily: "'Rye', serif",
  fontSize: '1.16rem',
  fontWeight: 400,
  letterSpacing: '0.03em',
  lineHeight: 1,
  textShadow: '0 1px 0 rgba(255, 235, 170, 0.6)',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  overflowWrap: 'normal'
};

const inlineContent: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
};

const posterContent: CSSProperties = {
  gap: '6px'
};

const inlineTitleRow: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  minWidth: 0
};

const posterTitleRow: CSSProperties = {
  justifyContent: 'space-between',
  gap: '12px'
};

const GOLD_TEXT = '#ffd97a';
const GOLD_INK = '#3b2008';

/* The prize ticket: the amount the player is playing for, set like a coupon. The fill is the
   progress bar's own (white to green toward $20, green to gold toward $50, gold metal once
   $50 is earned), so the ticket and the bar always agree. Notched sides read as a ticket;
   the drop shadow lives on a wrapper because the notch mask would clip a box-shadow. */
const prizeTicketShadow: CSSProperties = {
  flexShrink: 0,
  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35))'
};

const TICKET_NOTCH =
  'radial-gradient(circle at 0 50%, transparent 5px, #000 5.5px) left / 51% 100% no-repeat, radial-gradient(circle at 100% 50%, transparent 5px, #000 5.5px) right / 51% 100% no-repeat';

const prizeTicketStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  // Wide enough for EARNED, so the strip doesn't shift when the caption changes.
  minWidth: 76,
  padding: '7px 12px',
  borderRadius: 6,
  WebkitMask: TICKET_NOTCH,
  mask: TICKET_NOTCH
};

const prizeTicketCaption: CSSProperties = {
  fontSize: '0.56rem',
  fontWeight: 800,
  letterSpacing: '0.14em',
  lineHeight: 1,
  textTransform: 'uppercase',
  opacity: 0.78
};

const prizeTicketAmount: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1.45rem',
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums'
};

const inlineCashBackStatus: CSSProperties = {
  color: '#fff4d7',
  fontSize: '0.74rem',
  fontWeight: 800,
  lineHeight: 1.1,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap'
};

// Read aloud, never drawn: screen readers hear "$20 earned" where the strip shows "$20 ✓".
const screenReaderOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap'
};

// Sized, coloured and weighted like the bold text it ends (the stroke thickens the outline
// glyph to match), nudged onto its baseline.
const inlineCashBackCheck: CSSProperties = {
  fontSize: '1.15em',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  marginInlineStart: '0.15em',
  verticalAlign: '-0.2em'
};

const inlineCashBackEnd: CSSProperties = {
  marginLeft: 'auto',
  color: 'rgba(255,255,255,0.72)',
  fontSize: '0.66rem',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap'
};

const goldInkText: CSSProperties = {
  color: '#3b2008',
  textShadow: '0 1px 0 rgba(255, 235, 170, 0.6)'
};

/* Breaks between words only (never mid-word) and balances a two-line title. */
const inlinePromoName = {
  flex: 1,
  fontSize: '0.82rem',
  fontWeight: 900,
  color: '#ffffff',
  whiteSpace: 'normal',
  overflow: 'visible',
  textOverflow: 'clip',
  overflowWrap: 'break-word',
  textWrap: 'balance',
  lineHeight: 1.1,
  minWidth: 0
} as CSSProperties;

const posterPromoName: CSSProperties = {
  color: '#1a0e06',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textShadow: '0 1px 0 rgba(255, 235, 170, 0.35)'
};

const inlineHoursBadge: CSSProperties = {
  fontSize: '0.76rem',
  fontWeight: 900,
  color: '#fff4d7',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  flexShrink: 0
};

const posterHoursBadge: CSSProperties = {
  color: '#4a230a',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.78rem',
  fontWeight: 400,
  letterSpacing: '0.11em'
};

const inlineTrack: CSSProperties = {
  position: 'relative',
  height: 5,
  backgroundColor: 'rgba(30, 21, 15, 0.46)',
  borderRadius: 3,
  overflow: 'hidden',
  boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.28)'
};

const posterTrack: CSSProperties = {
  height: 6,
  borderRadius: 999,
  border: '1px solid rgba(74, 35, 10, 0.55)',
  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.45)'
};

const inlineFill: CSSProperties = {
  height: '100%',
  borderRadius: 2,
  background: 'linear-gradient(90deg, #fff1b6, #ffd15a)',
  width: '0%'
};

const posterFill: CSSProperties = {
  borderRadius: 999,
  boxShadow: 'inset 0 1px 0 rgba(255, 225, 180, 0.24)'
};

/* On a narrow strip the status drops under the end date (still right-aligned) instead of
   either one truncating. */
const inlineHelperRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '2px 8px',
  minWidth: 0
};

/* The status is the half of the message the value above it needs ("to $20"), so it never
   truncates. */
const inlineStatusText: CSSProperties = {
  color: '#fff4d7',
  fontSize: '0.72rem',
  fontWeight: 800,
  lineHeight: 1.1,
  minHeight: 8,
  marginLeft: 'auto',
  whiteSpace: 'nowrap'
};

const posterStatusText: CSSProperties = {
  color: '#4a230a',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.72rem',
  fontWeight: 400,
  letterSpacing: '0.08em',
  textTransform: 'uppercase'
};

const inlineHelperText: CSSProperties = {
  color: 'rgba(255,255,255,0.9)',
  fontSize: '0.68rem',
  fontWeight: 800,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap'
};

const posterHelperText: CSSProperties = {
  color: '#4a230a',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.68rem',
  fontWeight: 400,
  letterSpacing: '0.16em',
  lineHeight: 1,
  textTransform: 'uppercase',
  opacity: 0.82
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The window's last day is the day before the reset day ("Sunday" -> "Sat"). */
const getWindowEndDay = (resetDayLabel: string): string => {
  const resetIndex = WEEKDAYS.indexOf(resetDayLabel);
  if (resetIndex < 0) return resetDayLabel.slice(0, 3);
  return WEEKDAYS[(resetIndex + 6) % 7].slice(0, 3);
};
